import React, { useState, useEffect } from 'react';
import { crmDataService } from '../utils/crmDataService';
import DeleteConfirmationModal from '../../../../components/common/DeleteConfirmationModal';
import '../styles/crmSettings.css'; // Reusing existing styles

function ConditionalFieldManager() {
    const [teams, setTeams] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);

    const [selectedTeam, setSelectedTeam] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedSubcategory, setSelectedSubcategory] = useState('');

    const [fields, setFields] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    const [showModal, setShowModal] = useState(false);
    const [editingField, setEditingField] = useState(null);
    const [fieldForm, setFieldForm] = useState({
        field_name: '',
        field_code: '',
        field_type: 'text',
        is_required: false,
        field_options: [], // For dropdowns
        placeholder_text: '',
        help_text: '',
        display_order: 0
    });

    // Delete Modal State
    const [deleteModal, setDeleteModal] = useState({
        isOpen: false,
        fieldId: null,
        fieldName: null
    });

    // Load Teams on mount
    useEffect(() => {
        loadTeams();
    }, []);

    const loadTeams = async () => {
        try {
            const data = await crmDataService.getTeams();
            setTeams(data || []);
        } catch (err) {
            console.error('Failed to load teams:', err);
            setError('Failed to load teams');
        }
    };

    // Load Categories when Team changes
    useEffect(() => {
        if (selectedTeam) {
            loadCategories(selectedTeam);
        } else {
            setCategories([]);
            setSubcategories([]);
            setSelectedCategory('');
            setSelectedSubcategory('');
        }
    }, [selectedTeam]);

    const loadCategories = async (teamId) => {
        try {
            const data = await crmDataService.getCategoriesByTeam(teamId);
            setCategories(data || []);
        } catch (err) {
            console.error('Failed to load categories:', err);
        }
    };

    // Load Subcategories when Category changes
    useEffect(() => {
        if (selectedCategory) {
            loadSubcategories(selectedCategory);
        } else {
            setSubcategories([]);
            setSelectedSubcategory('');
        }
    }, [selectedCategory]);

    const loadSubcategories = async (categoryId) => {
        try {
            const data = await crmDataService.getSubcategoriesByCategory(categoryId);
            setSubcategories(data || []);
        } catch (err) {
            console.error('Failed to load subcategories:', err);
        }
    };

    // Load Fields when Subcategory changes
    useEffect(() => {
        if (selectedSubcategory) {
            loadFields(selectedSubcategory);
        } else {
            setFields([]);
        }
    }, [selectedSubcategory]);

    const loadFields = async (subcategoryId) => {
        setLoading(true);
        try {
            const data = await crmDataService.getFieldsBySubcategory(subcategoryId);
            setFields(data || []);
            setError(null);
        } catch (err) {
            console.error('Failed to load fields:', err);
            setError('Failed to load fields');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveField = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...fieldForm,
                subcategory_id: selectedSubcategory,
                // Ensure field code is snake_case
                field_code: fieldForm.field_code || fieldForm.field_name.toLowerCase().replace(/\s+/g, '_')
            };

            if (editingField) {
                await crmDataService.updateField(editingField.id, payload);
                setSuccessMessage('Field updated successfully');
            } else {
                await crmDataService.createField(payload);
                setSuccessMessage('Field created successfully');
            }

            setShowModal(false);
            setEditingField(null);
            resetForm();
            loadFields(selectedSubcategory);
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError('Failed to save field: ' + err.message);
        }
    };

    const handleDeleteField = (field) => {
        setDeleteModal({
            isOpen: true,
            fieldId: field.id,
            fieldName: field.field_name
        });
    };

    const handleConfirmDelete = async () => {
        try {
            await crmDataService.deleteField(deleteModal.fieldId);
            setSuccessMessage('Field deleted successfully');
            loadFields(selectedSubcategory);
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError('Failed to delete field: ' + err.message);
        } finally {
            setDeleteModal({ isOpen: false, fieldId: null, fieldName: null });
        }
    };

    const resetForm = () => {
        setFieldForm({
            field_name: '',
            field_code: '',
            field_type: 'text',
            is_required: false,
            field_options: [],
            placeholder_text: '',
            help_text: '',
            display_order: fields.length + 1
        });
    };

    const openAddModal = () => {
        setEditingField(null);
        resetForm();
        setShowModal(true);
    };

    const openEditModal = (field) => {
        setEditingField(field);
        setFieldForm({
            field_name: field.field_name,
            field_code: field.field_code,
            field_type: field.field_type,
            is_required: field.is_required,
            field_options: field.field_options || [],
            placeholder_text: field.placeholder_text || '',
            help_text: field.help_text || '',
            display_order: field.display_order
        });
        setShowModal(true);
    };

    const handleOptionChange = (index, value) => {
        const newOptions = [...fieldForm.field_options];
        newOptions[index] = value;
        setFieldForm({ ...fieldForm, field_options: newOptions });
    };

    const addOption = () => {
        setFieldForm({ ...fieldForm, field_options: [...fieldForm.field_options, ''] });
    };

    const removeOption = (index) => {
        const newOptions = fieldForm.field_options.filter((_, i) => i !== index);
        setFieldForm({ ...fieldForm, field_options: newOptions });
    };

    return (
        <div className="conditional-field-manager">
            <div className="filter-section">
                <div className="form-group">
                    <label>Team</label>
                    <select value={selectedTeam} onChange={(e) => setSelectedTeam(e.target.value)}>
                        <option value="">Select Team</option>
                        {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                </div>
                <div className="form-group">
                    <label>Category</label>
                    <select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        disabled={!selectedTeam}
                    >
                        <option value="">Select Category</option>
                        {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </div>
                <div className="form-group">
                    <label>Subcategory</label>
                    <select
                        value={selectedSubcategory}
                        onChange={(e) => setSelectedSubcategory(e.target.value)}
                        disabled={!selectedCategory}
                    >
                        <option value="">Select Subcategory</option>
                        {subcategories.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                </div>
            </div>

            {error && (
                <div className="alert alert-error">
                    <span>{error}</span>
                    <button onClick={() => setError(null)}>×</button>
                </div>
            )}

            {successMessage && (
                <div className="alert alert-success">
                    <span>{successMessage}</span>
                </div>
            )}

            {selectedSubcategory ? (
                <div className="fields-list-section">
                    <div className="section-header">
                        <h3>Fields for Subcategory</h3>
                        <button className="btn-primary" onClick={openAddModal}>+ Add Field</button>
                    </div>

                    {loading ? (
                        <div className="loading-spinner"></div>
                    ) : (
                        <table className="settings-table">
                            <thead>
                                <tr>
                                    <th>Label</th>
                                    <th>Type</th>
                                    <th>Required</th>
                                    <th>Order</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {fields.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="empty-state">No fields defined for this subcategory</td>
                                    </tr>
                                ) : (
                                    fields.map(field => (
                                        <tr key={field.id}>
                                            <td>{field.field_name}</td>
                                            <td>{field.field_type}</td>
                                            <td>{field.is_required ? 'Yes' : 'No'}</td>
                                            <td>{field.display_order}</td>
                                            <td>
                                                <button className="btn-link" onClick={() => openEditModal(field)}>Edit</button>
                                                <button className="btn-link text-danger" onClick={() => handleDeleteField(field)}>Delete</button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            ) : (
                <div className="empty-state">
                    Please select a subcategory to manage fields.
                </div>
            )}

            {/* Field Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingField ? 'Edit Field' : 'Add New Field'}</h2>
                            <button onClick={() => setShowModal(false)}>×</button>
                        </div>

                        <form onSubmit={handleSaveField}>
                            <div className="form-group">
                                <label>Field Label *</label>
                                <input
                                    type="text"
                                    value={fieldForm.field_name}
                                    onChange={(e) => setFieldForm({ ...fieldForm, field_name: e.target.value })}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>Field Code (Auto-generated if empty)</label>
                                <input
                                    type="text"
                                    value={fieldForm.field_code}
                                    onChange={(e) => setFieldForm({ ...fieldForm, field_code: e.target.value })}
                                    placeholder="e.g., device_serial_number"
                                />
                            </div>

                            <div className="form-group">
                                <label>Type *</label>
                                <select
                                    value={fieldForm.field_type}
                                    onChange={(e) => setFieldForm({ ...fieldForm, field_type: e.target.value })}
                                >
                                    <option value="text">Text</option>
                                    <option value="number">Number</option>
                                    <option value="dropdown">Dropdown</option>
                                    <option value="date">Date</option>
                                    <option value="checkbox">Checkbox</option>
                                    <option value="textarea">Text Area</option>
                                </select>
                            </div>

                            {fieldForm.field_type === 'dropdown' && (
                                <div className="form-group">
                                    <label>Options</label>
                                    {fieldForm.field_options.map((option, index) => (
                                        <div key={index} className="option-row" style={{ display: 'flex', gap: '10px', marginBottom: '5px' }}>
                                            <input
                                                type="text"
                                                value={option}
                                                onChange={(e) => handleOptionChange(index, e.target.value)}
                                                placeholder={`Option ${index + 1}`}
                                                required
                                            />
                                            <button type="button" className="btn-secondary" onClick={() => removeOption(index)}>Remove</button>
                                        </div>
                                    ))}
                                    <button type="button" className="btn-secondary" onClick={addOption}>+ Add Option</button>
                                </div>
                            )}

                            <div className="form-group">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={fieldForm.is_required}
                                        onChange={(e) => setFieldForm({ ...fieldForm, is_required: e.target.checked })}
                                    />
                                    Required Field
                                </label>
                            </div>

                            <div className="form-group">
                                <label>Placeholder Text</label>
                                <input
                                    type="text"
                                    value={fieldForm.placeholder_text}
                                    onChange={(e) => setFieldForm({ ...fieldForm, placeholder_text: e.target.value })}
                                />
                            </div>

                            <div className="form-group">
                                <label>Help Text</label>
                                <input
                                    type="text"
                                    value={fieldForm.help_text}
                                    onChange={(e) => setFieldForm({ ...fieldForm, help_text: e.target.value })}
                                />
                            </div>

                            <div className="form-group">
                                <label>Display Order</label>
                                <input
                                    type="number"
                                    value={fieldForm.display_order}
                                    onChange={(e) => setFieldForm({ ...fieldForm, display_order: parseInt(e.target.value) })}
                                />
                            </div>

                            <div className="modal-actions">
                                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                                <button type="submit" className="btn-primary">{editingField ? 'Update' : 'Create'} Field</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <DeleteConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, fieldId: null, fieldName: null })}
                onConfirm={handleConfirmDelete}
                itemName={deleteModal.fieldName}
            />
        </div>
    );
}

export default ConditionalFieldManager;
